/**
 * Nakama Client Module
 * Handles Nakama client instance, authentication, and session management
 */

import { Client } from '@heroiclabs/nakama-js';
import type { Session } from '@heroiclabs/nakama-js';
import { getCurrentConfig } from '../../config/nakama.config';

/**
 * Nakama Client Manager
 * Manages the Nakama client instance, session, and authentication
 */
class NakamaClientManager {
  private client: Client | null = null;
  private session: Session | null = null;

  /**
   * Initialize and get the Nakama client instance
   * Creates a new client if one doesn't exist
   */
  getClient(): Client {
    if (!this.client) {
      const config = getCurrentConfig();
      this.client = new Client(
        config.serverKey,
        config.host,
        config.port.toString(),
        config.useSSL
      );
      console.log('[NakamaClient] Client initialized', {
        host: config.host,
        port: config.port,
        useSSL: config.useSSL,
      });
    }
    return this.client;
  }

  /**
   * Get the current session
   */
  getSession(): Session | null {
    return this.session;
  }

  /**
   * Set the current session after authentication
   */
  setSession(session: Session): void {
    this.session = session;
    console.log('[NakamaClient] Session set', {
      userId: session.user_id,
      username: session.username,
    });
  }

  /**
   * Authenticate with custom ID
   * Uses Nakama's custom authentication with vars for backend validation
   * 
   * @param nakamaCustomId - Player's custom ID (e.g., "nakama_student_ana_brazil_001")
   * @param gameName - Game name (e.g., "ponder-flight-game")
   * @param worldName - World/instance name (e.g., "World_1"). Optional for admin role.
   * @param role - Optional user role ("student", "moderator", or "admin")
   * @param password - Optional password. Required for admin role; validated server-side.
   * @returns Session object
   */
  async authenticateCustom(
    nakamaCustomId: string,
    gameName: string,
    worldName: string,
    role?: string,
    password?: string
  ): Promise<Session> {
    const client = this.getClient();
    
    try {
      console.log('[NakamaClient] Authenticating with custom ID:', {
        nakamaCustomId,
        gameName,
        worldName,
        role,
      });

      const vars: Record<string, string> = {
        game_name: gameName,
        world_name: worldName,
      };
      
      if (role) {
        vars.role = role;
      }

      if (password) {
        vars.password = password;
      }

      const session = await client.authenticateCustom(nakamaCustomId, true, undefined, vars);

      this.setSession(session);
      console.log('[NakamaClient] Authentication successful');
      return session;
    } catch (error) {
      console.error('[NakamaClient] Authentication failed:', error);
      throw error;
    }
  }

  /**
   * Update player account after authentication: the join's second step, which
   * sets the player's class, role and name on their Nakama account. The
   * server refuses as an error — by its code where a player can meet it
   * (account_unavailable, domains/join-refusals.ts) — and answers nothing
   * the step reads otherwise.
   *
   * @param gameName - Game name
   * @param worldName - World name
   * @param role - Optional user role ("student" or "moderator")
   * @param cohortId - Optional cohort ID override (for moderators joining specific teams)
   * @param displayName - Optional display name override (for session-link logins)
   */
  async updatePlayerAccountAfterAuthenticate(
    gameName: string,
    worldName: string,
    role?: string,
    cohortId?: string,
    displayName?: string
  ): Promise<void> {
    if (!this.session) {
      throw new Error('Must be authenticated before updating account');
    }

    const client = this.getClient();
    
    try {
      console.log('[NakamaClient] Updating player account:', { gameName, worldName, role, cohortId, displayName });

      const payload: Record<string, string> = {
        game_name: gameName,
        world_name: worldName,
      };
      
      if (role) {
        payload.role = role;
      }
      
      if (cohortId) {
        payload.cohort_id = cohortId;
      }

      if (displayName) {
        payload.display_name = displayName;
      }

      await client.rpc(this.session, 'update_player_account_after_authenticate', payload);
      console.log('[NakamaClient] Account updated successfully');
    } catch (error) {
      console.error('[NakamaClient] Failed to update account:', error);
      throw error;
    }
  }

  /**
   * Check if currently has a valid session
   */
  isAuthenticated(): boolean {
    return this.session !== null;
  }

  /**
   * Reset the client manager (useful for logout)
   */
  reset(): void {
    this.session = null;
    this.client = null;
    console.log('[NakamaClient] Client reset');
  }
}

// Export singleton instance
export const nakamaClientManager = new NakamaClientManager();
